// src/components/Timer/index.js
import React from 'react';
import * as Colors from 'src/styles/colors';

const SECOND = 1000;

function parseClosingAt(closingAt) {
  if (!closingAt) return null;
  const dt =
    typeof closingAt === 'string' || typeof closingAt === 'number'
      ? new Date(closingAt)
      : closingAt;
  const ms = dt?.getTime?.();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, ms - Date.now());
}

function msToParts(ms) {
  if (!Number.isFinite(ms)) return { d: 0, h: 0, m: 0, s: 0 };
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { d, h, m, s };
}

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Hybrid behavior:
 * - If `remainingMs` is provided => locally tick down from that value and
 *   resync whenever it changes.
 * - Else if `closingAt` is provided => self-tick from `closingAt`.
 */
export default function TimeRemaining({
  remainingMs = null,
  closingAt = null,
  tickMs = 1000,
}) {
  const controlled = Number.isFinite(remainingMs);

  // baseline used for controlled ticking
  const baseMsRef = React.useRef(null);
  const baseAtRef = React.useRef(null);

  // initial display
  const initial = controlled ? Math.max(0, remainingMs) : parseClosingAt(closingAt);
  const [displayMs, setDisplayMs] = React.useState(initial);

  // resync baseline whenever the controlled value changes
  React.useEffect(() => {
    if (controlled) {
      const next = Math.max(0, remainingMs);
      baseMsRef.current = next;
      baseAtRef.current = Date.now();
      setDisplayMs(next);
    } else {
      setDisplayMs(parseClosingAt(closingAt));
    }
  }, [controlled, remainingMs, closingAt]);

  // ticking
  React.useEffect(() => {
    const id = setInterval(() => {
      if (controlled) {
        const baseMs = baseMsRef.current;
        const baseAt = baseAtRef.current;
        if (!Number.isFinite(baseMs) || !Number.isFinite(baseAt)) return;
        const elapsed = Date.now() - baseAt;
        setDisplayMs(Math.max(0, baseMs - elapsed));
      } else {
        setDisplayMs(parseClosingAt(closingAt));
      }
    }, tickMs);

    return () => clearInterval(id);
  }, [controlled, closingAt, tickMs]);

  if (!Number.isFinite(displayMs)) {
    return <span style={{ opacity: 0.6 }}>—</span>;
  }

  const { d, h, m, s } = msToParts(displayMs);
  const urgent = displayMs <= 30 * SECOND;
  const color = urgent ? Colors.elRed : 'inherit';

  const text =
    d > 0
      ? `${d}:${pad2(h)}:${pad2(m)}:${pad2(s)}`
      : h > 0
      ? `${h}:${pad2(m)}:${pad2(s)}`
      : `${pad2(m)}:${pad2(s)}`;

  return (
    <span style={{ color, fontVariantNumeric: 'tabular-nums' }}>
      {text}
    </span>
  );
}
